import React, { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Avatar, TextField, Typography } from '@mui/material';
import { resolveAvatarUrl } from '../../services/api';
import AvatarFallback from '../AvatarFallback';
import { formatMemberSince } from '../../utils/statsFormat';

// =============================================================================
// ProfileView — the modern profile body, shared by ProfileCard (viewing OTHERS,
// read-only) and ProfileDialog's account view (viewing SELF, editable).
//
// Layout (top → bottom):
//   - Banner band: a solid user-chosen background color (profile.bannerColor) or
//     a theme default. A large avatar overlaps the bottom edge of the banner.
//   - Identity: username (in usernameColor), a role badge shown ONLY for
//     admin/owner/guest, "Member since", and a country flag (from an ISO-2 code
//     resolved server-side from IP).
//   - Bio: a short paragraph (≤200 chars). In editable mode it's an inline editor
//     with a live counter; empty + editable shows an "Add a bio" affordance.
//
// Deliberately NO stats, NO links, NO age/gender — those were the 2000s-chat bits
// we're moving away from. Theme-aware via palette.custom.* (except the user's
// chosen banner color); banner text uses a contrast-picked ink so any color reads.
// =============================================================================

const BIO_MAX = 200;

// ISO-2 country code → flag emoji (regional-indicator pair). Returns '' for a
// missing/malformed code so the caller can omit it. e.g. 'US' → 🇺🇸.
function countryToFlag(code) {
  if (!code || typeof code !== 'string' || code.length !== 2) return '';
  const cc = code.toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  const base = 0x1f1e6; // regional indicator 'A'
  return String.fromCodePoint(
    base + (cc.charCodeAt(0) - 65),
    base + (cc.charCodeAt(1) - 65),
  );
}

// Pick black or white ink for legible text on top of an arbitrary hex background,
// using the WCAG relative-luminance heuristic. Falls back to white for anything
// unparseable (matches the dark theme default banner).
function readableInk(hex) {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return '#ffffff';
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return L > 0.5 ? '#000000' : '#ffffff';
}

// Roles worth badging. Regular signed-up members show no role (per product
// decision) — only staff and guests get a badge.
const BADGE_ROLES = new Set(['admin', 'owner', 'guest']);

const useStyles = makeStyles()((theme) => ({
  root: {
    // Let the banner run edge-to-edge; the consuming dialog supplies the frame.
    margin: theme.spacing(-1, -1, 0),
  },
  banner: {
    height: 96,
    borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-end',
  },
  // Avatar overlaps the banner's bottom edge, sitting half-in / half-out.
  avatarWrap: {
    position: 'absolute',
    left: theme.spacing(3),
    bottom: theme.spacing(-4.5),
  },
  avatar: {
    width: 96,
    height: 96,
    fontSize: '2.2rem',
    border: `3px solid ${theme.palette.background.paper}`,
    boxShadow: theme.shadows[3],
  },
  // Avatars paint their own pixels — kill MUI's default color fill. '&&' doubles
  // specificity to beat .MuiAvatar-colorDefault regardless of injection order.
  avatarPhoto: {
    '&&': { background: theme.palette.custom?.avatarBg || 'transparent' },
  },
  body: {
    padding: theme.spacing(6, 3, 1),
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  name: {
    fontSize: '1.35rem',
    fontWeight: 800,
    lineHeight: 1.15,
    letterSpacing: '-0.01em',
  },
  flag: {
    fontSize: '1.15rem',
    lineHeight: 1,
  },
  badge: {
    fontSize: '0.65rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    padding: theme.spacing(0.25, 0.9),
    borderRadius: 999,
    color: theme.palette.custom?.accentContrastText || '#000',
    backgroundColor: theme.palette.primary.main,
  },
  since: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  bio: {
    marginTop: theme.spacing(2),
    fontSize: '0.95rem',
    lineHeight: 1.5,
    color: theme.palette.text.primary,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  bioEmpty: {
    marginTop: theme.spacing(2),
    fontSize: '0.9rem',
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
  },
  bioEditor: {
    marginTop: theme.spacing(2),
  },
  counter: {
    display: 'block',
    textAlign: 'right',
    fontSize: '0.72rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
  },
}));

/**
 * @param profile  { bio, bannerColor, usernameColor, role, avatarUrl, country, memberSince }
 *                 (any field may be null/absent).
 * @param editable bool — self view: renders the inline bio editor.
 * @param onBioChange (bio: string) => void — called (debounced by the parent, or
 *                 on blur here) when the user edits their bio. Self view only.
 */
const ProfileView = ({ profile = {}, editable = false, onBioChange }) => {
  const { classes, cx, theme } = useStyles();

  const name = profile.username || profile.name || 'user';
  const usernameColor = profile.usernameColor || undefined;
  const role = (profile.role || '').toLowerCase();
  const showBadge = BADGE_ROLES.has(role);
  const since = formatMemberSince(profile.memberSince);
  const flag = countryToFlag(profile.country);

  // Banner: user's solid color, else the theme's translucent accent wash.
  const bannerBg = profile.bannerColor || theme.palette.custom?.accentWash || theme.palette.action.hover;
  const bannerInk = profile.bannerColor ? readableInk(profile.bannerColor) : (theme.palette.custom?.ink || '#fff');

  // Local editable bio state so typing is responsive; commit up on blur.
  const [draft, setDraft] = useState(profile.bio || '');
  useEffect(() => {
    setDraft(profile.bio || '');
  }, [profile.bio]);

  const commitBio = () => {
    const next = draft.slice(0, BIO_MAX);
    if (next !== (profile.bio || '') && onBioChange) onBioChange(next);
  };

  return (
    <div className={classes.root}>
      <div className={classes.banner} style={{ background: bannerBg, color: bannerInk }}>
        <div className={classes.avatarWrap}>
          <Avatar
            src={resolveAvatarUrl(profile.avatarUrl || profile.avatar)}
            alt={name}
            className={cx(classes.avatar, classes.avatarPhoto)}
          >
            <AvatarFallback letter={name.charAt(0).toUpperCase()} />
          </Avatar>
        </div>
      </div>

      <div className={classes.body}>
        <div className={classes.nameRow}>
          <span className={classes.name} style={usernameColor ? { color: usernameColor } : undefined}>
            {name}
          </span>
          {flag && (
            <span className={classes.flag} title={profile.country} aria-label={`Country: ${profile.country}`}>
              {flag}
            </span>
          )}
          {showBadge && (
            <span className={classes.badge}>{role === 'owner' ? 'Owner' : role === 'admin' ? 'Admin' : 'Guest'}</span>
          )}
        </div>
        {since && <div className={classes.since}>Member since {since}</div>}

        {editable ? (
          <div className={classes.bioEditor}>
            <TextField
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, BIO_MAX))}
              onBlur={commitBio}
              placeholder="Add a bio — tell the room a little about you"
              multiline
              minRows={2}
              maxRows={5}
              fullWidth
              variant="outlined"
              size="small"
              slotProps={{ htmlInput: { maxLength: BIO_MAX } }}
            />
            <span className={classes.counter}>
              {draft.length}/{BIO_MAX}
            </span>
          </div>
        ) : profile.bio ? (
          <Typography className={classes.bio}>{profile.bio}</Typography>
        ) : (
          <Typography className={classes.bioEmpty}>No bio yet.</Typography>
        )}
      </div>
    </div>
  );
};

export default ProfileView;
